import { Container, Graphics, Text } from 'pixi.js'
import type { DoorMark } from '@/sim/arena'
import { mapPlan, type MapPlan } from '@/sim/route'
import type { World } from '@/sim/world'
import { tuning } from '@/tuning'
import { label, placeLeft, P } from './ui'
import { OATH } from './oathMetal'

function markColor(mark: DoorMark): number {
  switch (mark) {
    case 'blade': return P.ember
    case 'veil': return P.veil
    case 'combat': return P.bone
    case 'gift': return P.gold
    case 'hard': return P.ember
    case 'elite': return OATH.rim
    case 'boss': return P.red
    default: { const _e: never = mark; return _e }
  }
}

/**
 * Exits-phase route strip. Informational only — the player still walks into a door.
 * Hidden during town, fighting, reward, rite, title, and pause.
 */
export class RouteMap {
  root = new Container()
  private g = new Graphics()
  private texts: Text[] = []
  private key = ''
  private paused = false
  private suppressed = false

  constructor(layer: Container) {
    this.root.visible = false
    this.root.addChild(this.g)
    layer.addChild(this.root)
  }

  relayout(): void { this.key = '' }
  setPaused(paused: boolean): void { if (this.paused !== paused) { this.paused = paused; this.key = '' } }
  setSuppressed(suppressed: boolean): void {
    if (this.suppressed !== suppressed) { this.suppressed = suppressed; this.key = '' }
  }

  update(world: World): void {
    const run = world.session.run
    const room = world.rooms[world.roomIndex]
    const show = !this.suppressed && !this.paused
      && world.scenario === 'loop'
      && world.roomPhase === 'exits'
      && !!run && run.result === 'active'
      && !run.pendingReward && !run.pendingRite && !run.pendingShop && !run.pendingMystery
      && (room.exits?.length ?? 0) > 0
    this.root.visible = show
    if (!show) return
    const path = run.roomHistory.map(v => v.id).join('>')
    const plan = mapPlan(world.rooms, room.id)
    const next = plan.doors.map(d => `${d.mark}:${d.dest}`).join('|')
    const nextKey = `${tuning.view.width}|${path}|${next}|${plan.then ?? ''}`
    if (nextKey === this.key) return
    this.key = nextKey
    this.paint(plan)
  }

  private paint(plan: MapPlan): void {
    this.clear()
    const W = tuning.view.width
    const rowH = 14
    const pad = 8
    const rows = plan.doors.length + (plan.then ? 1 : 0)
    const boxH = pad * 2 + rows * rowH
    const boxY = 52
    const boxW = Math.min(W - 16, 280)
    const boxX = Math.round((W - boxW) / 2)

    this.g.roundRect(boxX, boxY, boxW, boxH, 2).fill({ color: P.void, alpha: 0.86 })
    this.g.rect(boxX, boxY, 2, boxH).fill({ color: P.gold })

    plan.doors.forEach((ex, i) => {
      const y = boxY + pad + rowH * i + rowH / 2
      // The doors already face the room. NORTH / EAST on this strip was a compass sitting on the plan.
      const mark = label(ex.markLabel, 'meta', markColor(ex.mark))
      placeLeft(mark, boxX + 10, y)
      this.add(mark)
      const name = label(ex.dest, 'meta', P.bone)
      placeLeft(name, boxX + 52, y)
      this.add(name)
    })

    if (plan.then) {
      const y = boxY + pad + rowH * plan.doors.length + rowH / 2
      const then = label(plan.then, 'meta', P.dim)
      placeLeft(then, boxX + 10, y)
      this.add(then)
    }
  }

  private clear(): void {
    for (const t of this.texts) t.destroy()
    this.texts = []
    this.root.removeChildren()
    this.g.destroy()
    this.g = new Graphics()
    this.root.addChild(this.g)
  }

  private add(t: Text): void { this.texts.push(t); this.root.addChild(t) }
}
